import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, setAccessToken, getAccessToken } from '@/lib/api';
import type { UserProfile } from '@mawkaa/types';

// Auth response without refreshToken (now in httpOnly cookie)
interface AuthResponse {
  user: UserProfile;
  accessToken: string;
  expiresIn: number;
}

interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  requestOTP: (phone: string) => Promise<void>;
  verifyOTP: (phone: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const response = await api.post<AuthResponse>('/auth/login', { email, password });
          // Store access token in memory (more secure than localStorage)
          setAccessToken(response.accessToken);
          // Refresh token is set as httpOnly cookie by the API
          set({ user: response.user, isAuthenticated: true });
        } finally {
          set({ isLoading: false });
        }
      },

      register: async (email: string, password: string, firstName?: string, lastName?: string) => {
        set({ isLoading: true });
        try {
          const response = await api.post<AuthResponse>('/auth/register', {
            email,
            password,
            firstName,
            lastName,
          });
          // Store access token in memory
          setAccessToken(response.accessToken);
          // Refresh token is set as httpOnly cookie by the API
          set({ user: response.user, isAuthenticated: true });
        } finally {
          set({ isLoading: false });
        }
      },

      requestOTP: async (phone: string) => {
        set({ isLoading: true });
        try {
          await api.post('/auth/otp/request', { phone });
        } finally {
          set({ isLoading: false });
        }
      },

      verifyOTP: async (phone: string, code: string) => {
        set({ isLoading: true });
        try {
          const response = await api.post<AuthResponse>('/auth/otp/verify', { phone, code });
          // Store access token in memory
          setAccessToken(response.accessToken);
          // Refresh token is set as httpOnly cookie by the API
          set({ user: response.user, isAuthenticated: true });
        } finally {
          set({ isLoading: false });
        }
      },

      logout: async () => {
        try {
          // Logout will clear the httpOnly cookie on the server
          await api.post('/auth/logout', {});
        } catch {
          // Ignore errors during logout
        }
        // Clear access token from memory
        setAccessToken(null);
        set({ user: null, isAuthenticated: false });
      },

      fetchUser: async () => {
        try {
          const response = await api.get<{ user: UserProfile }>('/users/me');
          set({ user: response.user, isAuthenticated: true });
        } catch {
          set({ user: null, isAuthenticated: false });
        }
      },

      updateProfile: async (data: Partial<UserProfile>) => {
        const response = await api.patch<{ user: UserProfile }>('/users/me', data);
        set({ user: response.user });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
